import { NAVIGABLE_ARTEFACTS, OPEN_CUE, POLITENESS_PREFIXES } from '../pack/lexicons';

import { stripLeadingPhrase } from './cues';
import { hasOperandEvidence } from './operand-evidence';
import { findProtectedAtoms } from './protected-atoms';

/**
 * `navigationTarget`: whether `open …` names something specific enough to navigate to.
 *
 * `open` is the one navigation cue that is also an ordinary English word, so the
 * catalog refuses to let it win merely because text follows. It needs either a
 * non-empty protected atom — a URL, an identifier, a quoted title — or a singular
 * navigable artefact noun **plus** further evidence naming which one.
 *
 * `open the roadmap page` names a page. `open the page` does not, and falls to
 * `S-AMBIGUOUS-OPEN`.
 */

/** Returns the span after an `open` cue, or `null` when the query does not start with one. */
export const getOpenSpan = (qMatch: string): string | null => {
	const politeness = stripLeadingPhrase(qMatch, POLITENESS_PREFIXES);
	const withoutPoliteness = politeness ? politeness.rest : qMatch;
	const cue = stripLeadingPhrase(withoutPoliteness, [OPEN_CUE]);

	return cue ? cue.rest : null;
};

/** Removes the first whole-word occurrence of an artefact noun, returning the remainder. */
const removeArtefactNoun = (span: string): string | null => {
	const folded = span.toLowerCase();

	for (const artefact of [...NAVIGABLE_ARTEFACTS].sort(
		(left, right) => right.length - left.length,
	)) {
		const index = folded.indexOf(artefact);

		if (index === -1) {
			continue;
		}

		const before = span[index - 1];
		const after = span[index + artefact.length];
		const isWholeWord =
			(before === undefined || before === ' ') && (after === undefined || after === ' ');

		if (isWholeWord) {
			return `${span.slice(0, index)} ${span.slice(index + artefact.length)}`;
		}
	}

	return null;
};

export const resolveNavigationTarget = (qMatch: string): boolean => {
	const span = getOpenSpan(qMatch);

	// Nothing worth navigating to, whatever shape it has. This is what stops an empty
	// quoted range — `open ""` — from counting as a complete protected atom.
	if (span === null || !hasOperandEvidence(span)) {
		return false;
	}

	if (findProtectedAtoms(span).length > 0) {
		return true;
	}

	const withoutArtefact = removeArtefactNoun(span);

	return withoutArtefact !== null && hasOperandEvidence(withoutArtefact);
};
